import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bird, Book, Flame, Wind, Siren, X, Phone, Leaf } from "lucide-react";
import DigitalDiary from "./DigitalDiary";
import VentBox from "./VentBox";
import BreathingSphere from "./BreathingSphere";
import HelplineModal from "./HelplineModal"; // NEW

import { useAuth } from "../../context/AuthContext";

export default function SanctuaryFab() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTool, setActiveTool] = useState(null); // 'diary', 'vent', 'breathe', 'helpline'

    // Safety check: Only show if logged in
    const location = useLocation();

    // Explicitly hide on Landing Page ("/") or Chat Page ("/chat")
    if (!user || location.pathname === "/" || (location.pathname.startsWith("/chat") && !isOpen)) {
        // We hide it on home and chat unless it's open (it won't be open if it's hidden though)
        // Actually, user wants it ALWAYS available in the nav center.
        if (location.pathname === "/") return null;
    }

    const handleSOS = () => {
        // Immediate redirect for safety
        window.location.href = "https://www.google.com/search?q=weather+forecast";
    };

    const menuItems = [
        { id: "diary", label: "Diary", icon: Book, color: "bg-amber-100 text-amber-600", onClick: () => setActiveTool("diary") },
        { id: "vent", label: "Vent", icon: Flame, color: "bg-red-100 text-red-600", onClick: () => setActiveTool("vent") },
        { id: 'breathe', label: 'Breathe', icon: Wind, color: 'bg-blue-100 text-blue-600', onClick: () => setActiveTool('breathe') },
        { id: 'helpline', label: 'Help', icon: Phone, color: 'bg-green-100 text-green-600', onClick: () => setActiveTool('helpline') }, // NEW
        { id: "sos", label: "SOS", icon: Siren, color: "bg-red-500 text-white animate-pulse", onClick: handleSOS },
    ];

    const isChatPage = location.pathname.startsWith("/chat");

    return (
        <>
            {/* Zen Backdrop Blur */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 z-[90] bg-black/20 dark:bg-black/40 backdrop-blur-md"
                    />
                )}
            </AnimatePresence>

            <div className="fixed left-1/2 -translate-x-1/2 md:left-auto md:right-56 md:translate-x-0 bottom-4 md:bottom-8 z-[100] flex flex-col items-center">
                {/* Menu Items (Lotus Bloom) */}
                <AnimatePresence>
                    {isOpen && (
                        <div className="relative w-0 h-0">
                            {menuItems.map((item, index) => {
                                // Calculate angles: Slightly more towards the left/top to avoid right-edge clipping
                                const angle = 175 - (index * 38); // 175, 137, 99, 61, 23
                                const rad = angle * (Math.PI / 180);
                                const radius = 115; // Increased radius slightly
                                const tx = radius * Math.cos(rad);
                                const ty = -radius * Math.sin(rad);

                                return (
                                    <motion.button
                                        key={item.id}
                                        initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                                        animate={{ 
                                            opacity: 1, 
                                            x: tx,
                                            y: ty,
                                            scale: 1,
                                        }}
                                        exit={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                                        transition={{ 
                                            type: "spring",
                                            stiffness: 260,
                                            damping: 20,
                                            delay: index * 0.04 
                                        }}
                                        onClick={() => {
                                            setIsOpen(false);
                                            item.onClick();
                                        }}
                                        className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 group"
                                    >
                                        <div className={`p-3 sm:p-4 rounded-full shadow-xl transition-transform hover:scale-110 active:scale-90 ${item.color} border-2 border-white dark:border-gray-900`}>
                                            <item.icon className="w-5 h-5 sm:w-6 h-6" />
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-white drop-shadow-md">
                                            {item.label}
                                        </span>
                                    </motion.button>
                                );
                            })}
                        </div>
                    )}
                </AnimatePresence>

                {/* Main FAB (The Sun) */}
                <motion.button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`relative group flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-500 ${isOpen ? 'bg-brand-blue rotate-45' : 'bg-white dark:bg-gray-800'}`}
                    animate={isOpen ? { scale: 1.1 } : { y: [0, -3, 0], scale: 1 }}
                    transition={isOpen ? {} : { repeat: Infinity, duration: 4, ease: "easeInOut" }}
                >
                    <div className="absolute inset-0 rounded-full bg-brand-blue/10 animate-pulse transition-opacity duration-700" style={{ opacity: isOpen ? 0 : 1 }}></div>
                    {isOpen ? (
                        <X className="w-7 h-7 text-white" />
                    ) : (
                        <Leaf className="w-7 h-7 text-brand-blue" />
                    )}
                </motion.button>
            </div>

            {/* Tools / Modals */}
            {activeTool === "diary" && <DigitalDiary onClose={() => setActiveTool(null)} />}
            {activeTool === "vent" && <VentBox onClose={() => setActiveTool(null)} />}
            {activeTool === "breathe" && <BreathingSphere onClose={() => setActiveTool(null)} />}
            {activeTool === "helpline" && <HelplineModal onClose={() => setActiveTool(null)} />}
        </>
    );
}
